import * as DialogPrimitive from '@radix-ui/react-dialog';
import * as PopoverPrimitive from '@radix-ui/react-popover';
import {
  Ban,
  Building2,
  CalendarClock,
  Check,
  CircleHelp,
  FlaskConical,
  FlaskConicalOff,
  History,
  Lock,
  Minus,
  Pencil,
  Plus,
  ScrollText,
  Search,
  UserRoundCog,
  Users,
  X,
} from 'lucide-react';
import * as React from 'react';
import { toast } from 'sonner';
import { Callout } from '@/components/patterns/callout';
import { DataTable, Paginacao, TabelaPainel, type Coluna } from '@/components/patterns/data-table';
import { flagDoFiltro } from '@/lib/rules';
import { FilterBar, FilterBusca, FilterFlagsInstalacao, FilterLimpar, FilterPeriodo, FilterSelect } from '@/components/patterns/filter-bar';
import { EstadoVazio } from '@/components/patterns/states';
import { InstalacaoFlags, PapelBadge } from '@/components/patterns/status-badge';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Field, Input } from '@/components/ui/field';
import { Skeleton } from '@/components/ui/skeleton';
import { Switch } from '@/components/ui/switch';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip';
import {
  adminDataSource,
  type AuditoriaInstalacao,
  type FiltrosInstalacao,
  type InstalacaoEstado,
  type UsuarioComCapabilities,
  type UsuarioKeycloak,
} from '@/data/admin';
import { useAsyncAction } from '@/hooks/use-async-action';
import { noPeriodo, PERIODO_PRESETS } from '@/lib/periodo';
import { cn } from '@/lib/utils';

/**
 * Administração — capabilities, estado do PAN por instalação e capacidade de
 * agendamento. Modal (não página): aberto pelo dropdown do usuário, no
 * estilo "Configurações" do sustenta-ai-app — nav de seções à esquerda,
 * conteúdo rolável à direita, um único X fecha tudo.
 *
 * Não usa `useResource`/`dataSource`: são ferramentas de escrita restritas
 * por capability, sem sentido no modo demo (não há Keycloak real por trás).
 * Cada seção busca sob demanda, contra `apps/admin` diretamente
 * (`config.adminBaseUrl`), e some do menu se o usuário não tem a capability
 * daquela seção especificamente.
 */

const CAPS_CAPABILITIES = ['admin', 'sysadmin', 'sre'];
const CAPS_INSTALACOES = ['estado-instalacao', 'sysadmin', 'sre'];
// Capacidade de agendamento afeta o sistema inteiro de uma vez — só
// `sysadmin` de verdade, nem `sre` nem `estado-instalacao` bastam aqui
// (o backend em `apps/admin/agendamento_config` já exige o mesmo).
const CAPS_AGENDAMENTO = ['sysadmin'];

type SecaoId = 'capabilities' | 'instalacoes' | 'agendamento';

function temAlguma(roles: string[] | null, precisa: string[]): boolean {
  if (!roles) return false;
  return precisa.some((role) => roles.includes(role));
}

export function AdministracaoDialog({
  aberto,
  aoFechar,
  roles,
}: {
  aberto: boolean;
  aoFechar: () => void;
  roles: string[] | null;
}) {
  const podeCapabilities = temAlguma(roles, CAPS_CAPABILITIES);
  const podeInstalacoes = temAlguma(roles, CAPS_INSTALACOES);
  const podeAgendamento = temAlguma(roles, CAPS_AGENDAMENTO);

  const secoes = React.useMemo(() => {
    const lista: Array<{ id: SecaoId; rotulo: string; icone: typeof Users }> = [];
    if (podeCapabilities) lista.push({ id: 'capabilities', rotulo: 'Capabilities', icone: Users });
    if (podeInstalacoes) lista.push({ id: 'instalacoes', rotulo: 'Instalações', icone: Building2 });
    if (podeAgendamento) lista.push({ id: 'agendamento', rotulo: 'Agendamento', icone: CalendarClock });
    return lista;
  }, [podeCapabilities, podeInstalacoes, podeAgendamento]);

  const [secaoAtiva, setSecaoAtiva] = React.useState<SecaoId | null>(null);
  const secao = secaoAtiva && secoes.some((item) => item.id === secaoAtiva) ? secaoAtiva : (secoes[0]?.id ?? null);
  const secaoAtual = secoes.find((item) => item.id === secao);

  return (
    <Dialog open={aberto} onOpenChange={(valor) => !valor && aoFechar()}>
      <DialogPrimitive.Portal>
        <DialogPrimitive.Overlay className="fixed inset-0 z-50 bg-bg-overlay duration-slow ease-standard data-[state=open]:animate-in data-[state=open]:fade-in-0 data-[state=closed]:animate-out data-[state=closed]:fade-out-0" />
        <DialogPrimitive.Content
          className={cn(
            'fixed left-1/2 top-1/2 z-50 flex h-[min(44rem,90vh)] w-[min(90rem,96vw)] -translate-x-1/2 -translate-y-1/2 flex-col overflow-hidden rounded-card border border-border bg-bg-surface shadow-lg',
            'duration-slow ease-standard data-[state=open]:animate-in data-[state=open]:fade-in-0 data-[state=open]:zoom-in-95 data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=closed]:zoom-out-95',
          )}
        >
          <DialogPrimitive.Title className="sr-only">Administração</DialogPrimitive.Title>
          <DialogPrimitive.Description className="sr-only">
            Capabilities de usuários, estado do PAN por instalação e capacidade de agendamento.
          </DialogPrimitive.Description>

          {roles === null ? null : secoes.length === 0 ? (
            <>
              <div className="flex items-center justify-between border-b border-border px-5 py-4">
                <span className="text-card-title">Administração</span>
                <DialogPrimitive.Close className="rounded-button p-2 text-icon transition-colors duration-fast ease-standard hover:bg-bg-subtle hover:text-on-surface">
                  <X className="size-4" aria-hidden="true" />
                  <span className="sr-only">Fechar</span>
                </DialogPrimitive.Close>
              </div>
              <div className="flex flex-1 items-center justify-center p-6">
                <EstadoVazio
                  titulo="Nenhuma capability administrativa"
                  descricao="Sua conta não tem `admin` nem `estado-instalacao`. Peça a alguém com `admin` para conceder a capability necessária."
                />
              </div>
            </>
          ) : (
            <div className="flex min-h-0 flex-1">
              {/* Nav de seções — mesmo papel do "CONFIGURAÇÕES" do sustenta-ai-app. */}
              <nav
                aria-label="Seções da administração"
                className="flex w-56 shrink-0 flex-col gap-0.5 border-r border-border bg-bg-subtle p-3"
              >
                <span className="px-2 pb-2 pt-1 text-micro font-bold uppercase tracking-[0.06em] text-on-surface-muted">
                  Administração
                </span>
                {secoes.map((item) => {
                  const Icone = item.icone;
                  const ativa = secao === item.id;
                  return (
                    <button
                      key={item.id}
                      type="button"
                      role="tab"
                      aria-selected={ativa}
                      onClick={() => setSecaoAtiva(item.id)}
                      className={cn(
                        'flex items-center gap-2.5 rounded-button px-2.5 py-2 text-left text-body transition-colors duration-fast ease-standard',
                        ativa ? 'bg-primary-wash font-bold text-primary' : 'text-on-surface hover:bg-bg-surface',
                      )}
                    >
                      <Icone className="size-4 shrink-0" aria-hidden="true" />
                      {item.rotulo}
                    </button>
                  );
                })}
              </nav>

              {/* Conteúdo da seção — rola independente da nav. */}
              <div className="flex min-w-0 flex-1 flex-col">
                <div className="flex items-center justify-between border-b border-border px-5 py-4">
                  <span className="text-card-title">{secaoAtual?.rotulo ?? 'Administração'}</span>
                  <DialogPrimitive.Close className="rounded-button p-2 text-icon transition-colors duration-fast ease-standard hover:bg-bg-subtle hover:text-on-surface">
                    <X className="size-4" aria-hidden="true" />
                    <span className="sr-only">Fechar</span>
                  </DialogPrimitive.Close>
                </div>
                <div className="pan-scroll-area min-h-0 flex-1 overflow-y-auto p-5">
                  {secao === 'capabilities' ? <PainelCapabilities ehSysadmin={roles?.includes('sysadmin') ?? false} /> : null}
                  {secao === 'instalacoes' ? <PainelInstalacoes ehSysadmin={roles?.includes('sysadmin') ?? false} /> : null}
                  {secao === 'agendamento' ? <PainelAgendamento /> : null}
                </div>
              </div>
            </div>
          )}
        </DialogPrimitive.Content>
      </DialogPrimitive.Portal>
    </Dialog>
  );
}

const CARACTERES_MINIMOS = 2;
const DEBOUNCE_MS = 300;

/** Hook de busca remota com debounce — mesmo padrão do diálogo de personificação. */
function useBuscaRemota<T>(buscar: (termo: string, signal: AbortSignal) => Promise<T[]>) {
  const [termo, setTermo] = React.useState('');
  const [resultados, setResultados] = React.useState<T[]>([]);
  const [carregando, setCarregando] = React.useState(false);
  const [erro, setErro] = React.useState<string | null>(null);

  React.useEffect(() => {
    const valor = termo.trim();
    if (valor.length < CARACTERES_MINIMOS) {
      setResultados([]);
      setErro(null);
      setCarregando(false);
      return;
    }
    setCarregando(true);
    const controller = new AbortController();
    const idDebounce = window.setTimeout(() => {
      buscar(valor, controller.signal)
        .then((itens) => {
          setResultados(itens);
          setErro(null);
        })
        .catch((causa: unknown) => {
          if (causa instanceof DOMException && causa.name === 'AbortError') return;
          setResultados([]);
          setErro(causa instanceof Error ? causa.message : 'Não foi possível completar a busca.');
        })
        .finally(() => setCarregando(false));
    }, DEBOUNCE_MS);
    return () => {
      window.clearTimeout(idDebounce);
      controller.abort();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [termo]);

  return { termo, setTermo, resultados, carregando, erro, limpar: () => setResultados([]) };
}

/** Linhas fantasma — mesma altura de uma linha de usuário OU de capability
 * (nome+detalhe à esquerda, badge/switch à direita), pra não ter layout shift
 * quando o dado real chegar. */
function LinhasCarregando({ linhas = 4 }: { linhas?: number }) {
  return (
    <div className="flex flex-col gap-3 p-2" role="status" aria-label="Carregando">
      {Array.from({ length: linhas }).map((_, indice) => (
        <div key={indice} className="flex items-center justify-between gap-3 px-1">
          <div className="flex min-w-0 flex-1 flex-col gap-1.5">
            <Skeleton className="h-3.5 w-36" />
            <Skeleton className="h-2.5 w-48" />
          </div>
          <Skeleton className="h-5 w-11 shrink-0 rounded-full" />
        </div>
      ))}
    </div>
  );
}

const CAPABILIDADES_GERENCIAVEIS: Array<{ valor: string; rotulo: string; descricao: string }> = [
  { valor: 'admin', rotulo: 'Administrador', descricao: 'Gerencia capabilities de outros usuários — inclusive esta tela.' },
  {
    valor: 'estado-instalacao',
    rotulo: 'Estado da instalação',
    descricao: 'Habilita/desabilita o PAN por instalação e importa instalações novas.',
  },
  { valor: 'gestao-novidades', rotulo: 'Gestão de novidades', descricao: 'Cria, edita e remove novidades.' },
  {
    valor: 'personificar-instalacao',
    rotulo: 'Personificar instalação',
    descricao: 'Assume acesso de administrador completo sobre qualquer instalação do sistema.',
  },
  {
    valor: 'sysadmin',
    rotulo: 'Sysadmin',
    descricao:
      'Acesso total de engenharia, incluindo a capacidade de agendamento. Só quem já é sysadmin pode conceder ou revogar.',
  },
];

/** Soma/remove uma capability de UM usuário dentro da lista já carregada —
 * evita recarregar a lista inteira (e reexibir skeleton) por causa de um
 * único toggle. Some da lista se ficar com zero capabilities; entra na lista
 * na hora se for a primeira. */
function atualizarCapabilityNaLista(
  lista: UsuarioComCapabilities[] | null,
  usuario: UsuarioKeycloak,
  capability: string,
  incluir: boolean,
): UsuarioComCapabilities[] | null {
  if (!lista) return lista;
  const existente = lista.find((item) => item.keycloakUserId === usuario.keycloakUserId);

  if (!existente) {
    if (!incluir) return lista;
    return [...lista, { ...usuario, capabilities: [capability] }].sort((a, b) =>
      a.nome.localeCompare(b.nome, 'pt-BR'),
    );
  }

  const capabilities = incluir
    ? [...new Set([...existente.capabilities, capability])]
    : existente.capabilities.filter((item) => item !== capability);

  if (capabilities.length === 0) return lista.filter((item) => item.keycloakUserId !== usuario.keycloakUserId);
  return lista.map((item) => (item.keycloakUserId === usuario.keycloakUserId ? { ...item, capabilities } : item));
}

function PainelCapabilities({ ehSysadmin }: { ehSysadmin: boolean }) {
  const busca = useBuscaRemota<UsuarioKeycloak>((termo, signal) => adminDataSource.buscarUsuariosKeycloak(termo, signal));
  const [selecionado, setSelecionado] = React.useState<UsuarioKeycloak | null>(null);
  const [capsAtuais, setCapsAtuais] = React.useState<string[] | null>(null);
  const [carregandoCaps, setCarregandoCaps] = React.useState(false);
  const [erroCaps, setErroCaps] = React.useState<string | null>(null);
  const [capacidadeEmAcao, setCapacidadeEmAcao] = React.useState<string | null>(null);

  const recarregarCaps = React.useCallback((usuario: UsuarioKeycloak) => {
    setCarregandoCaps(true);
    setErroCaps(null);
    adminDataSource
      .obterCapabilities(usuario.keycloakUserId)
      .then((resposta) => setCapsAtuais(resposta.capabilities))
      .catch((causa: unknown) => {
        setCapsAtuais(null);
        setErroCaps(causa instanceof Error ? causa.message : 'Não foi possível carregar as capabilities do usuário.');
      })
      .finally(() => setCarregandoCaps(false));
  }, []);

  const [usuariosComCaps, setUsuariosComCaps] = React.useState<UsuarioComCapabilities[] | null>(null);
  const [carregandoLista, setCarregandoLista] = React.useState(true);
  const [erroLista, setErroLista] = React.useState<string | null>(null);

  const recarregarLista = React.useCallback(() => {
    setCarregandoLista(true);
    setErroLista(null);
    adminDataSource
      .listarUsuariosComCapabilities()
      .then(setUsuariosComCaps)
      .catch((causa: unknown) => {
        setUsuariosComCaps(null);
        setErroLista(causa instanceof Error ? causa.message : 'Não foi possível carregar a lista de usuários.');
      })
      .finally(() => setCarregandoLista(false));
  }, []);

  React.useEffect(() => {
    recarregarLista();
  }, [recarregarLista]);

  const [dropdownAberto, setDropdownAberto] = React.useState(false);

  function selecionar(usuario: UsuarioKeycloak) {
    setSelecionado(usuario);
    // Limpa TEXTO e resultados — só a lista some (`busca.limpar`) deixava o
    // campo com a busca antiga na tela, e sem resultado nenhum pra mostrar
    // isso caía direto na mensagem de "nenhum usuário encontrado", mesmo a
    // busca tendo funcionado (o usuário já aparece selecionado ao lado).
    busca.setTermo('');
    busca.limpar();
    setDropdownAberto(false);
    recarregarCaps(usuario);
  }

  async function alternar(capability: string, concedida: boolean) {
    if (!selecionado) return;
    const usuario = selecionado;
    const proximoValor = !concedida;

    setCapacidadeEmAcao(capability);
    // Otimista: só a badge/switch dessa capability muda, na hora — sem
    // recarregar `capsAtuais`/`usuariosComCaps` do servidor (era isso que
    // piscava skeleton na tela inteira a cada clique). Desfaz os dois estados
    // se o servidor recusar.
    setCapsAtuais((atual) => {
      const base = atual ?? [];
      return proximoValor ? [...new Set([...base, capability])] : base.filter((item) => item !== capability);
    });
    setUsuariosComCaps((atual) => atualizarCapabilityNaLista(atual, usuario, capability, proximoValor));

    try {
      if (concedida) {
        await adminDataSource.revogarCapability(usuario.keycloakUserId, capability);
      } else {
        await adminDataSource.concederCapability(usuario.keycloakUserId, capability);
      }
    } catch (causa) {
      setCapsAtuais((atual) => {
        const base = atual ?? [];
        return proximoValor ? base.filter((item) => item !== capability) : [...new Set([...base, capability])];
      });
      setUsuariosComCaps((atual) => atualizarCapabilityNaLista(atual, usuario, capability, concedida));
      toast.error(
        `Não foi possível ${proximoValor ? 'conceder' : 'revogar'} '${capability}': ${
          causa instanceof Error ? causa.message : String(causa)
        }`,
      );
    } finally {
      setCapacidadeEmAcao(null);
    }
  }

  const capsPorUsuario = React.useMemo(() => {
    const mapa = new Map<string, string[]>();
    for (const usuario of usuariosComCaps ?? []) mapa.set(usuario.keycloakUserId, usuario.capabilities);
    return mapa;
  }, [usuariosComCaps]);

  // Busca ativa (2+ caracteres) só controla o DROPDOWN de sugestão — a lista
  // de quem já tem capability continua sempre visível no corpo do card,
  // navegável mesmo sem digitar nada (sumia inteira quando isso dependia do
  // popover estar aberto, que só abre com o campo em foco).
  const emBusca = busca.termo.trim().length >= CARACTERES_MINIMOS;

  function linhaUsuario(usuario: UsuarioKeycloak) {
    const capabilities = capsPorUsuario.get(usuario.keycloakUserId) ?? [];
    return (
      <li key={usuario.keycloakUserId}>
        <button
          type="button"
          role="option"
          aria-selected={selecionado?.keycloakUserId === usuario.keycloakUserId}
          onClick={() => selecionar(usuario)}
          className="flex w-full flex-wrap items-center justify-between gap-x-3 gap-y-1 rounded-chip px-3 py-2 text-left transition-colors duration-fast ease-standard hover:bg-bg-subtle"
        >
          <span className="flex min-w-0 flex-col gap-0.5">
            <span className="text-cell font-bold">{usuario.nome || usuario.username}</span>
            <span className="text-micro font-normal uppercase tracking-[0.04em] text-on-surface-muted">
              {usuario.email}
            </span>
          </span>
          {capabilities.length > 0 ? (
            <span className="flex flex-wrap gap-1">
              {capabilities.map((capability) => (
                <Badge key={capability} tone="primary">
                  {CAPABILIDADES_GERENCIAVEIS.find((item) => item.valor === capability)?.rotulo ?? capability}
                </Badge>
              ))}
            </span>
          ) : null}
        </button>
      </li>
    );
  }

  return (
    <div className="grid items-start gap-6 xl:grid-cols-[minmax(0,1fr)_340px]">
      <Card>
        <CardHeader>
          <CardTitle>Usuários</CardTitle>
        </CardHeader>
        <CardContent>
          <PopoverPrimitive.Root open={dropdownAberto && emBusca} onOpenChange={setDropdownAberto}>
            <Field id="busca-usuario-capability" label="Filtrar por e-mail, username ou nome">
              {(props) => (
                // Ancora no <div> de verdade, não no `Field` (não é forwardRef
                // — o `Anchor asChild` do Radix precisa medir um nó DOM real
                // pra posicionar o dropdown embaixo do campo).
                <PopoverPrimitive.Anchor asChild>
                  <div className="relative">
                    <Search
                      className="pointer-events-none absolute left-3 top-1/2 size-4 -translate-y-1/2 text-icon"
                      aria-hidden="true"
                    />
                    <Input
                      {...props}
                      value={busca.termo}
                      placeholder="Deixe em branco pra ver quem já tem capability…"
                      onFocus={() => setDropdownAberto(true)}
                      onChange={(evento) => {
                        busca.setTermo(evento.target.value);
                        setSelecionado(null);
                        setDropdownAberto(true);
                      }}
                      className="pl-9"
                    />
                  </div>
                </PopoverPrimitive.Anchor>
              )}
            </Field>

            {/* Dropdown: só existe enquanto há busca ativa — é sugestão de
                digitação, não a lista permanente (essa fica sempre visível
                abaixo, dentro do próprio card). */}
            <PopoverPrimitive.Portal>
              <PopoverPrimitive.Content
                align="start"
                sideOffset={4}
                onOpenAutoFocus={(evento) => evento.preventDefault()}
                onCloseAutoFocus={(evento) => evento.preventDefault()}
                className="z-[60] w-[var(--radix-popover-trigger-width)] overflow-hidden rounded-input border border-border bg-bg-surface shadow-md duration-base ease-standard data-[state=open]:animate-in data-[state=open]:fade-in-0 data-[state=open]:zoom-in-95"
              >
                <div className="pan-scroll-area max-h-80 overflow-y-auto p-1">
                  {busca.carregando ? (
                    <LinhasCarregando linhas={3} />
                  ) : busca.erro ? (
                    <Callout tom="erro" className="m-1">
                      {busca.erro}
                    </Callout>
                  ) : busca.resultados.length > 0 ? (
                    <ul role="listbox" aria-label="Usuários encontrados" className="flex flex-col">
                      {busca.resultados.map(linhaUsuario)}
                    </ul>
                  ) : (
                    <p className="px-3 py-6 text-center text-cell text-on-surface-muted">
                      Nenhum usuário @nasajon.com.br encontrado para "{busca.termo.trim()}".
                    </p>
                  )}
                </div>
              </PopoverPrimitive.Content>
            </PopoverPrimitive.Portal>
          </PopoverPrimitive.Root>

          {/* Lista persistente: quem já tem capability — sempre visível,
              independente de foco/busca. Some de vista só enquanto o
              dropdown de busca está sugerindo algo (`emBusca`). */}
          {emBusca ? null : carregandoLista ? (
            <div className="mt-3 rounded-input border border-border">
              <LinhasCarregando />
            </div>
          ) : erroLista ? (
            <Callout tom="erro" className="mt-3">
              {erroLista}
            </Callout>
          ) : usuariosComCaps && usuariosComCaps.length > 0 ? (
            <ul
              role="listbox"
              aria-label="Usuários com capabilities"
              className="pan-scroll-area mt-3 max-h-96 overflow-y-auto rounded-input border border-border"
            >
              {usuariosComCaps.map(linhaUsuario)}
            </ul>
          ) : (
            <EstadoVazio
              className="mt-3"
              titulo="Ninguém tem capability ainda"
              descricao="Digite pra buscar um usuário @nasajon.com.br e conceder a primeira capability."
            />
          )}
        </CardContent>
      </Card>

      <Card as="aside" className="h-fit xl:sticky xl:top-0">
        <CardHeader>
          <CardTitle>{selecionado ? selecionado.nome || selecionado.username : 'Capabilities'}</CardTitle>
        </CardHeader>
        <CardContent>
          {!selecionado ? (
            <p className="text-cell text-on-surface-muted">Selecione um usuário para ver e alterar suas capabilities.</p>
          ) : carregandoCaps ? (
            <LinhasCarregando linhas={CAPABILIDADES_GERENCIAVEIS.length} />
          ) : erroCaps ? (
            <Callout tom="erro">{erroCaps}</Callout>
          ) : (
            <ul className="flex flex-col divide-y divide-border-subtle">
              {CAPABILIDADES_GERENCIAVEIS.map((capability) => {
                const concedida = Boolean(capsAtuais?.includes(capability.valor));
                const emAcao = capacidadeEmAcao === capability.valor;
                // Só sysadmin concede/revoga sysadmin — o backend já barra
                // isso com 403, mas desabilitar aqui evita um clique que só
                // vai dar erro.
                const bloqueadaPorGuarda = capability.valor === 'sysadmin' && !ehSysadmin;
                return (
                  <li key={capability.valor} className="flex items-center justify-between gap-3 py-2.5 first:pt-0 last:pb-0">
                    <div className="flex min-w-0 flex-col gap-0.5">
                      <span className="flex items-center gap-1.5 text-cell font-bold">
                        {capability.rotulo}
                        {concedida ? <Badge tone="success">Ativa</Badge> : null}
                      </span>
                      <span className="text-micro font-normal text-on-surface-muted">
                        {bloqueadaPorGuarda
                          ? `${capability.descricao} Só quem já é sysadmin pode alterar esta capability.`
                          : capability.descricao}
                      </span>
                    </div>
                    <Switch
                      checked={concedida}
                      disabled={emAcao || bloqueadaPorGuarda}
                      aria-label={`${concedida ? 'Revogar' : 'Conceder'} ${capability.rotulo}`}
                      onCheckedChange={() => alternar(capability.valor, concedida)}
                    />
                  </li>
                );
              })}
            </ul>
          )}
        </CardContent>
      </Card>
    </div>
  );
}

const ATIVO_OPCOES = [
  { valor: 'true', rotulo: 'Ativas' },
  { valor: 'false', rotulo: 'Inativas' },
];

/** Cabeçalho de coluna curto + "?" com a explicação no tooltip — mesmo
 * padrão já usado na célula de Região, só que aqui no cabeçalho. */
function CabecalhoComAjuda({ rotulo, explicacao }: { rotulo: string; explicacao: string }) {
  return (
    <span className="inline-flex items-center gap-1">
      {rotulo}
      <Tooltip>
        <TooltipTrigger asChild>
          <span
            tabIndex={0}
            aria-label={`O que é ${rotulo}`}
            className="inline-flex shrink-0 cursor-help normal-case text-icon hover:text-on-surface"
          >
            <CircleHelp className="size-3.5" aria-hidden="true" />
          </span>
        </TooltipTrigger>
        <TooltipContent>{explicacao}</TooltipContent>
      </Tooltip>
    </span>
  );
}

function PainelInstalacoes({ ehSysadmin }: { ehSysadmin: boolean }) {
  const [busca, setBusca] = React.useState('');
  const [zona, setZona] = React.useState<string | null>(null);
  const [versao, setVersao] = React.useState<string | null>(null);
  const [ativoFiltro, setAtivoFiltro] = React.useState<string | null>(null);
  const [anaFiltro, setAnaFiltro] = React.useState<string | null>(null);
  const [internoFiltro, setInternoFiltro] = React.useState<string | null>(null);
  const [pagina, setPagina] = React.useState(1);
  const [tamanhoPagina, setTamanhoPagina] = React.useState(50);
  const [itens, setItens] = React.useState<InstalacaoEstado[]>([]);
  const [total, setTotal] = React.useState(0);
  const [estado, setEstado] = React.useState<'carregando' | 'erro' | 'pronto'>('carregando');
  const [erro, setErro] = React.useState<Error | null>(null);
  const [linhaEmAcao, setLinhaEmAcao] = React.useState<number | null>(null);
  const [filtrosDisponiveis, setFiltrosDisponiveis] = React.useState<FiltrosInstalacao | null>(null);
  const [editando, setEditando] = React.useState<InstalacaoEstado | null>(null);
  const [auditando, setAuditando] = React.useState<InstalacaoEstado | null>(null);
  const [selecionadas, setSelecionadas] = React.useState<Set<string>>(new Set());

  // Seleção é só desta página/filtro — trocar qualquer um invalida o que
  // estava marcado, senão a barra de ação em massa mostraria uma contagem de
  // instalações que nem aparecem mais na tela.
  React.useEffect(() => {
    setSelecionadas(new Set());
  }, [busca, zona, versao, ativoFiltro, anaFiltro, internoFiltro, pagina, tamanhoPagina]);

  React.useEffect(() => {
    adminDataSource
      .filtrosInstalacao()
      .then(setFiltrosDisponiveis)
      .catch(() => setFiltrosDisponiveis(null));
  }, []);

  // Qualquer mudança de filtro (ou do tamanho da página) invalida a página
  // atual — sem isso, trocar de filtro na página 5 podia pedir um offset
  // além do fim do resultado novo.
  React.useEffect(() => {
    setPagina(1);
  }, [busca, zona, versao, ativoFiltro, anaFiltro, internoFiltro, tamanhoPagina]);

  const buscarInstalacoes = React.useCallback(
    (signal?: AbortSignal) => {
      setEstado('carregando');
      adminDataSource
        .buscarInstalacoesEstado(
          {
            q: busca.trim() || undefined,
            zona: zona ?? undefined,
            versao: versao ?? undefined,
            ativo: ativoFiltro === null ? undefined : ativoFiltro === 'true',
            ana: flagDoFiltro(anaFiltro),
            interno: flagDoFiltro(internoFiltro),
            limit: tamanhoPagina,
            offset: (pagina - 1) * tamanhoPagina,
          },
          signal,
        )
        .then((resultado) => {
          setItens(resultado.itens);
          setTotal(resultado.total);
          setEstado('pronto');
        })
        .catch((causa: unknown) => {
          if (causa instanceof DOMException && causa.name === 'AbortError') return;
          setErro(causa instanceof Error ? causa : new Error(String(causa)));
          setEstado('erro');
        });
    },
    [busca, zona, versao, ativoFiltro, anaFiltro, internoFiltro, pagina, tamanhoPagina],
  );

  React.useEffect(() => {
    const controller = new AbortController();
    const idDebounce = window.setTimeout(() => buscarInstalacoes(controller.signal), DEBOUNCE_MS);
    return () => {
      window.clearTimeout(idDebounce);
      controller.abort();
    };
  }, [buscarInstalacoes]);

  const totalPaginas = Math.max(1, Math.ceil(total / tamanhoPagina));

  const ativoAcao = useAsyncAction({ sucesso: null, contextoDoErro: 'Não foi possível atualizar a instalação' });
  const betaAcao = useAsyncAction({ sucesso: null, contextoDoErro: 'Não foi possível atualizar a instalação' });
  const bloqueioAcao = useAsyncAction({ sucesso: null, contextoDoErro: 'Não foi possível atualizar a instalação' });

  async function alternarAtivo(item: InstalacaoEstado) {
    setLinhaEmAcao(item.id);
    await ativoAcao.executar(async () => {
      await adminDataSource.editarInstalacao(item.id, { ativo: !item.ativo });
      buscarInstalacoes();
    });
    setLinhaEmAcao(null);
  }

  async function alternarBeta(item: InstalacaoEstado) {
    setLinhaEmAcao(item.id);
    await betaAcao.executar(async () => {
      await adminDataSource.editarInstalacao(item.id, { beta: !item.beta });
      buscarInstalacoes();
    });
    setLinhaEmAcao(null);
  }

  async function alternarBloqueada(item: InstalacaoEstado) {
    setLinhaEmAcao(item.id);
    await bloqueioAcao.executar(async () => {
      await adminDataSource.editarInstalacao(item.id, { bloqueada: !item.bloqueada });
      buscarInstalacoes();
    });
    setLinhaEmAcao(null);
  }

  type AcaoEmLote = 'ativo-on' | 'ativo-off' | 'beta-on' | 'beta-off';
  const [acaoEmLote, setAcaoEmLote] = React.useState<AcaoEmLote | null>(null);
  const emLoteAcao = useAsyncAction({ sucesso: null, contextoDoErro: 'Não foi possível concluir a ação em massa' });

  async function executarEmLote(chave: AcaoEmLote, alteracoes: { ativo?: boolean; beta?: boolean }) {
    const ids = [...selecionadas].map(Number);
    setAcaoEmLote(chave);
    await emLoteAcao.executar(async () => {
      const resultado = await adminDataSource.editarInstalacoesEmLote(ids, alteracoes);
      toast.success(
        `${resultado.atualizadas} instalaç${resultado.atualizadas === 1 ? 'ão atualizada' : 'ões atualizadas'}.`,
      );
      setSelecionadas(new Set());
      buscarInstalacoes();
    });
    setAcaoEmLote(null);
  }

  const filtrosAtivos = [
    busca.trim() !== '',
    zona !== null,
    versao !== null,
    ativoFiltro !== null,
    anaFiltro !== null,
    internoFiltro !== null,
  ].filter(Boolean).length;

  const colunas: Array<Coluna<InstalacaoEstado>> = [
    {
      id: 'instalacao',
      cabecalho: 'Instalação',
      ordenarPor: (item) => item.nome,
      celula: (item) => (
        <div className="flex flex-col gap-0.5">
          <span className="flex flex-wrap items-center gap-x-3 gap-y-1 font-bold">
            {item.nome}
            {item.bloqueada ? (
              <Badge tone="danger" icon={<Lock aria-hidden="true" />}>
                Bloqueada
              </Badge>
            ) : null}
            <InstalacaoFlags ana={item.ana} interno={item.interno} />
          </span>
          <span className="text-micro font-normal uppercase tracking-[0.04em] text-on-surface-muted">
            {item.codigo}
            {item.codigoManual ? ' · apelido' : ''} · ID {item.id}
          </span>
        </div>
      ),
    },
    {
      id: 'papel',
      cabecalho: 'Papel',
      largura: '150px',
      celula: (item) => <PapelBadge valor={item.papel} />,
    },
    {
      id: 'zona',
      cabecalho: 'Região',
      largura: '210px',
      secundaria: true,
      celula: (item) =>
        item.zona ? (
          <span className="flex items-center gap-1.5">
            {item.zona}
            {item.zonaClusterNome || item.zonaServidorNome ? (
              <Tooltip>
                <TooltipTrigger asChild>
                  <span
                    tabIndex={0}
                    aria-label={`O que é a região ${item.zona}`}
                    className="inline-flex shrink-0 cursor-help rounded-full text-icon hover:text-on-surface"
                  >
                    <CircleHelp className="size-3.5" aria-hidden="true" />
                  </span>
                </TooltipTrigger>
                <TooltipContent>
                  <dl className="flex flex-col gap-1.5">
                    {item.zonaClusterNome ? (
                      <div className="flex flex-col">
                        <dt className="font-bold">{item.zonaClusterNome}</dt>
                        <dd className="text-on-surface-muted">{item.zonaClusterTecnico ?? '—'}</dd>
                      </div>
                    ) : null}
                    {item.zonaServidorNome ? (
                      <div className="flex flex-col">
                        <dt className="font-bold">{item.zonaServidorNome}</dt>
                        <dd className="text-on-surface-muted">{item.zonaServidorTecnico ?? '—'}</dd>
                      </div>
                    ) : null}
                  </dl>
                </TooltipContent>
              </Tooltip>
            ) : null}
          </span>
        ) : (
          '—'
        ),
    },
    { id: 'versao', cabecalho: 'Versão', largura: '140px', secundaria: true, celula: (item) => item.versao ?? '—' },
    {
      id: 'ativo',
      cabecalho: (
        <CabecalhoComAjuda
          rotulo="Agendamento"
          explicacao="Quando desligada, a instalação some do hub inteiro (Início, Contratos, Agendar etc.) para o cliente — não só da tela de agendamento. Quem personifica a instalação continua usando normalmente, sem precisar ativar."
        />
      ),
      largura: '130px',
      celula: (item) => (
        <Switch
          checked={item.ativo}
          disabled={linhaEmAcao === item.id && ativoAcao.executando}
          aria-label={item.ativo ? 'Desabilitar agendamento' : 'Habilitar agendamento'}
          onCheckedChange={() => alternarAtivo(item)}
        />
      ),
    },
    {
      id: 'beta',
      cabecalho: (
        <CabecalhoComAjuda
          rotulo="Beta"
          explicacao="Quando ativado, essa instalação recebe a versão beta mais recente do ramo dela como recomendada, em vez de esperar a versão estável."
        />
      ),
      largura: '90px',
      celula: (item) => (
        <Switch
          checked={item.beta}
          disabled={linhaEmAcao === item.id && betaAcao.executando}
          aria-label={item.beta ? 'Remover da versão beta' : 'Incluir na versão beta'}
          onCheckedChange={() => alternarBeta(item)}
        />
      ),
    },
    ...(ehSysadmin
      ? [
          {
            id: 'bloqueada',
            cabecalho: (
              <CabecalhoComAjuda
                rotulo="Bloquear"
                explicacao="Trava global, só sysadmin: a instalação some do hub, não aceita agendamento e não pode ser personificada — para todos, inclusive sysadmin/sre."
              />
            ),
            largura: '110px',
            celula: (item: InstalacaoEstado) => (
              <Switch
                checked={item.bloqueada}
                disabled={linhaEmAcao === item.id && bloqueioAcao.executando}
                aria-label={item.bloqueada ? 'Desbloquear instalação' : 'Bloquear instalação'}
                onCheckedChange={() => alternarBloqueada(item)}
              />
            ),
          } satisfies Coluna<InstalacaoEstado>,
        ]
      : []),
    {
      id: 'acoes',
      cabecalho: 'Ações',
      largura: '170px',
      celula: (item) => (
        <div className="flex items-center gap-1">
          <Button variant="ghost" size="sm" onClick={() => setEditando(item)}>
            <Pencil aria-hidden="true" />
            Editar
          </Button>
          <Button variant="ghost" size="sm" onClick={() => setAuditando(item)}>
            <History aria-hidden="true" />
            Auditoria
          </Button>
        </div>
      ),
    },
  ];

  return (
    <>
      <TabelaPainel
        titulo="Instalações"
        contagem={estado === 'pronto' ? `${total} resultado(s)` : undefined}
        filtros={
          <FilterBar>
            <FilterBusca
              id="busca-instalacao-estado"
              label="Buscar instalação"
              valor={busca}
              aoMudar={setBusca}
              placeholder="Nome, código ou ID de qualquer tenant…"
            />
            <FilterSelect
              id="filtro-zona-instalacao"
              label="Região"
              valor={zona}
              aoMudar={setZona}
              rotuloTodos="Qualquer região"
              opcoes={(filtrosDisponiveis?.zonas ?? []).map((z) => ({ valor: z, rotulo: z }))}
            />
            <FilterSelect
              id="filtro-versao-instalacao"
              label="Sprint"
              valor={versao}
              aoMudar={setVersao}
              rotuloTodos="Qualquer sprint"
              opcoes={(filtrosDisponiveis?.versoes ?? []).map((v) => ({ valor: v, rotulo: v }))}
            />
            <FilterSelect
              id="filtro-ativo-instalacao"
              label="Status"
              valor={ativoFiltro}
              aoMudar={setAtivoFiltro}
              rotuloTodos="Ativas e inativas"
              opcoes={ATIVO_OPCOES}
            />
            <FilterFlagsInstalacao
              idBase="filtro-instalacao-estado"
              ana={anaFiltro}
              aoMudarAna={setAnaFiltro}
              interno={internoFiltro}
              aoMudarInterno={setInternoFiltro}
              mostrarAna={filtrosDisponiveis?.temAna ?? false}
              mostrarInterno={filtrosDisponiveis?.temInterno ?? false}
            />
            <FilterLimpar
              ativo={filtrosAtivos > 0}
              quantidade={filtrosAtivos}
              aoLimpar={() => {
                setBusca('');
                setZona(null);
                setVersao(null);
                setAtivoFiltro(null);
                setAnaFiltro(null);
                setInternoFiltro(null);
              }}
            />
          </FilterBar>
        }
        paginacao={
          estado === 'pronto' ? (
            <Paginacao
              pagina={pagina}
              totalPaginas={totalPaginas}
              aoMudar={setPagina}
              tamanhoPagina={tamanhoPagina}
              aoMudarTamanho={setTamanhoPagina}
            />
          ) : undefined
        }
      >
        <DataTable
          rotulo="Instalações"
          itens={itens}
          colunas={colunas}
          chave={(item) => String(item.id)}
          estado={estado}
          erro={erro}
          aoTentarDeNovo={() => buscarInstalacoes()}
          vazio={{
            titulo: 'Nenhuma instalação encontrada',
            descricao: 'Ajuste os filtros para ver outras instalações do sistema.',
          }}
          selecionadas={selecionadas}
          aoAlternarSelecao={(id, marcado) =>
            setSelecionadas((atual) => {
              const proximo = new Set(atual);
              if (marcado) proximo.add(id);
              else proximo.delete(id);
              return proximo;
            })
          }
          aoAlternarTodas={(marcado) =>
            setSelecionadas(marcado ? new Set(itens.map((item) => String(item.id))) : new Set())
          }
        />
      </TabelaPainel>

      {selecionadas.size > 0 ? (
        <div className="sticky bottom-3 z-10 mt-4 flex justify-center px-1">
          <div className="flex flex-wrap items-center gap-3 rounded-panel border border-border bg-bg-surface px-3.5 py-2.5 shadow-lg">
            <div className="flex items-center gap-2 pl-1">
              <span className="flex h-5 min-w-5 items-center justify-center rounded-full bg-primary px-1.5 text-micro font-bold text-on-primary">
                {selecionadas.size}
              </span>
              <span className="text-body font-bold">{selecionadas.size === 1 ? 'selecionada' : 'selecionadas'}</span>
            </div>
            <button
              type="button"
              onClick={() => setSelecionadas(new Set())}
              className="rounded-button p-1.5 text-icon transition-colors duration-fast ease-standard hover:bg-bg-subtle hover:text-on-surface"
              aria-label="Limpar seleção"
            >
              <X className="size-4" aria-hidden="true" />
            </button>
            <div className="h-6 w-px shrink-0 bg-border" />
            <div className="flex flex-wrap items-center gap-2">
              <Button
                variant="outline"
                size="sm"
                loading={acaoEmLote === 'ativo-on'}
                disabled={emLoteAcao.executando}
                onClick={() => executarEmLote('ativo-on', { ativo: true })}
              >
                <Check aria-hidden="true" />
                Habilitar agendamento
              </Button>
              <Button
                variant="outline"
                size="sm"
                loading={acaoEmLote === 'ativo-off'}
                disabled={emLoteAcao.executando}
                onClick={() => executarEmLote('ativo-off', { ativo: false })}
              >
                <Ban aria-hidden="true" />
                Desabilitar agendamento
              </Button>
              <Button
                variant="outline"
                size="sm"
                loading={acaoEmLote === 'beta-on'}
                disabled={emLoteAcao.executando}
                onClick={() => executarEmLote('beta-on', { beta: true })}
              >
                <FlaskConical aria-hidden="true" />
                Incluir em beta
              </Button>
              <Button
                variant="outline"
                size="sm"
                loading={acaoEmLote === 'beta-off'}
                disabled={emLoteAcao.executando}
                onClick={() => executarEmLote('beta-off', { beta: false })}
              >
                <FlaskConicalOff aria-hidden="true" />
                Remover de beta
              </Button>
            </div>
          </div>
        </div>
      ) : null}

      <EditarInstalacaoDialog instalacao={editando} aoFechar={() => setEditando(null)} aoSalvar={() => buscarInstalacoes()} />
      <AuditoriaInstalacaoDialog instalacao={auditando} aoFechar={() => setAuditando(null)} />
    </>
  );
}

function EditarInstalacaoDialog({
  instalacao,
  aoFechar,
  aoSalvar,
}: {
  instalacao: InstalacaoEstado | null;
  aoFechar: () => void;
  aoSalvar: () => void;
}) {
  const [codigo, setCodigo] = React.useState('');
  const [nome, setNome] = React.useState('');
  const acao = useAsyncAction({
    sucesso: 'Instalação atualizada.',
    contextoDoErro: 'Não foi possível salvar',
    aoConcluir: () => {
      aoSalvar();
      aoFechar();
    },
  });

  React.useEffect(() => {
    if (instalacao) {
      setCodigo(instalacao.codigo);
      setNome(instalacao.nome);
    }
  }, [instalacao]);

  function salvar() {
    if (!instalacao) return;
    void acao.executar(() =>
      adminDataSource.editarInstalacao(instalacao.id, { codigo: codigo.trim(), nome: nome.trim() }).then(() => undefined),
    );
  }

  return (
    <Dialog open={Boolean(instalacao)} onOpenChange={(aberto) => !aberto && aoFechar()}>
      <DialogContent className="z-[60]">
        <DialogHeader>
          <DialogTitle>Editar instalação</DialogTitle>
          <DialogDescription>
            Código e nome viram um apelido manual — depois de editado, a sincronização automática com o ambiente
            de nuvem não sobrescreve mais.
          </DialogDescription>
        </DialogHeader>

        <Field id="editar-codigo-instalacao" label="Código">
          {(props) => <Input {...props} value={codigo} onChange={(evento) => setCodigo(evento.target.value)} />}
        </Field>
        <Field id="editar-nome-instalacao" label="Nome">
          {(props) => <Input {...props} value={nome} onChange={(evento) => setNome(evento.target.value)} />}
        </Field>

        {acao.erro ? <Callout tom="erro">{acao.erro.message}</Callout> : null}

        <DialogFooter>
          <Button variant="outline" onClick={aoFechar}>
            Cancelar
          </Button>
          <Button variant="primary" loading={acao.executando} disabled={!codigo.trim() || !nome.trim()} onClick={salvar}>
            Salvar
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

/** Instalações de um mesmo horário não disparam juntas — o worker espaça a
 * atualização de cada uma por pelo menos 2 minutos, pra não martelar a
 * infra toda de uma vez. Numa janela de 1h isso permite no máximo 30
 * instalações (60 / 2), e é por isso que o teto abaixo é 30, não um
 * número redondo qualquer (o backend aplica o mesmo teto). */
const CAPACIDADE_MAXIMA_POR_HORARIO = 30;

/** Capacidade de agendamento — quantas instalações cabem por horário. Muda
 * na hora (o backend lê ao vivo, sem cache) e zera qualquer retenção de
 * horário em andamento — quem está no meio de marcar um horário é avisado
 * via WebSocket e precisa selecionar de novo. */
function PainelAgendamento() {
  const [valorAtual, setValorAtual] = React.useState<number | null>(null);
  const [rascunho, setRascunho] = React.useState<number>(0);
  const [estado, setEstado] = React.useState<'carregando' | 'erro' | 'pronto'>('carregando');
  const [erro, setErro] = React.useState<Error | null>(null);

  const carregar = React.useCallback((signal?: AbortSignal) => {
    setEstado('carregando');
    adminDataSource
      .buscarAgendamentoConfig(signal)
      .then((resultado) => {
        setValorAtual(resultado.maxSeats);
        setRascunho(resultado.maxSeats);
        setEstado('pronto');
      })
      .catch((causa: unknown) => {
        if (causa instanceof DOMException && causa.name === 'AbortError') return;
        setErro(causa instanceof Error ? causa : new Error(String(causa)));
        setEstado('erro');
      });
  }, []);

  React.useEffect(() => {
    const controller = new AbortController();
    carregar(controller.signal);
    return () => controller.abort();
  }, [carregar]);

  const acao = useAsyncAction({
    sucesso: null,
    contextoDoErro: 'Não foi possível atualizar a capacidade de agendamento',
  });

  async function salvar() {
    await acao.executar(async () => {
      const resultado = await adminDataSource.atualizarAgendamentoConfig(rascunho);
      setValorAtual(resultado.maxSeats);
      setRascunho(resultado.maxSeats);
      toast.success(
        `Capacidade atualizada para ${resultado.maxSeats} instalaç${resultado.maxSeats === 1 ? 'ão' : 'ões'} por horário. Retenções em andamento foram liberadas.`,
      );
    });
  }

  const alterado = valorAtual !== null && rascunho !== valorAtual;

  return (
    <Card className="max-w-xl">
      <CardHeader>
        <CardTitle>Capacidade de agendamento</CardTitle>
      </CardHeader>
      <CardContent>
        {estado === 'carregando' ? (
          <Skeleton className="h-10 w-48" />
        ) : estado === 'erro' ? (
          <Callout tom="erro">
            {erro?.message ?? 'Erro ao carregar.'}{' '}
            <button type="button" className="underline" onClick={() => carregar()}>
              Tentar de novo
            </button>
          </Callout>
        ) : (
          <div className="flex flex-col gap-4">
            <p className="text-cell text-on-surface-muted">
              Quantas instalações podem ser atualizadas ao mesmo tempo, em cada horário disponível para agendamento.
              Limitado a {CAPACIDADE_MAXIMA_POR_HORARIO}, para manter pelo menos 2 minutos de espaçamento entre uma
              atualização e a próxima dentro da mesma hora.
            </p>
            <div className="flex items-center gap-3">
              <Button
                variant="outline"
                size="icon"
                aria-label="Diminuir"
                disabled={rascunho <= 1}
                onClick={() => setRascunho((valor) => Math.max(1, valor - 1))}
              >
                <Minus aria-hidden="true" />
              </Button>
              <Input
                type="number"
                min={1}
                max={CAPACIDADE_MAXIMA_POR_HORARIO}
                value={rascunho}
                onChange={(evento) => {
                  const numero = Number(evento.target.value);
                  if (Number.isFinite(numero)) {
                    setRascunho(Math.min(CAPACIDADE_MAXIMA_POR_HORARIO, Math.max(1, Math.trunc(numero))));
                  }
                }}
                className="w-24 text-center tabular-nums"
                aria-label="Instalações simultâneas por horário"
              />
              <Button
                variant="outline"
                size="icon"
                aria-label="Aumentar"
                disabled={rascunho >= CAPACIDADE_MAXIMA_POR_HORARIO}
                onClick={() => setRascunho((valor) => Math.min(CAPACIDADE_MAXIMA_POR_HORARIO, valor + 1))}
              >
                <Plus aria-hidden="true" />
              </Button>
              <span className="text-cell text-on-surface-muted">instalações por horário</span>
            </div>

            {acao.erro ? <Callout tom="erro">{acao.erro.message}</Callout> : null}

            <div className="flex items-center gap-3">
              <Button variant="primary" loading={acao.executando} disabled={!alterado} onClick={salvar}>
                Salvar e aplicar agora
              </Button>
              {alterado ? (
                <Button variant="ghost" onClick={() => setRascunho(valorAtual ?? 0)}>
                  Descartar
                </Button>
              ) : null}
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
}

const ROTULO_OPERACAO: Record<AuditoriaInstalacao['operacao'], { rotulo: string; icone: typeof History }> = {
  personificar: { rotulo: 'Personificação', icone: UserRoundCog },
  habilitar: { rotulo: 'Habilitada p/ agendamento', icone: CalendarClock },
  beta: { rotulo: 'Versão beta', icone: ScrollText },
  bloquear: { rotulo: 'Bloqueio', icone: Lock },
};

function formatarDataHora(iso: string): string {
  try {
    return new Date(iso).toLocaleString('pt-BR', { dateStyle: 'short', timeStyle: 'short' });
  } catch {
    return iso;
  }
}

function descreverDetalhe(item: AuditoriaInstalacao): string | null {
  if (item.operacao === 'personificar') return null;
  const de = item.detalhe.de;
  const para = item.detalhe.para;
  if (typeof de !== 'boolean' || typeof para !== 'boolean') return null;
  const rotulo = (valor: boolean) => (valor ? 'ativado' : 'desativado');
  return `${rotulo(de)} → ${rotulo(para)}`;
}

/** Rastro de auditoria de UMA instalação — personificar, habilitar, beta.
 * Cada linha diz quem fez, o quê e quando; sem edição, só leitura. Mesmo
 * padrão de listagem do resto do app (filtro de período + tabela +
 * paginação), não um feed solto — e o período segue a mesma regra de teto do
 * Histórico (até 31 dias, truncado pelo início; "qualquer período" também
 * respeita o teto). */
function AuditoriaInstalacaoDialog({
  instalacao,
  aoFechar,
}: {
  instalacao: InstalacaoEstado | null;
  aoFechar: () => void;
}) {
  const [itens, setItens] = React.useState<AuditoriaInstalacao[]>([]);
  const [total, setTotal] = React.useState(0);
  const [estado, setEstado] = React.useState<'carregando' | 'erro' | 'pronto'>('carregando');
  const [erro, setErro] = React.useState<Error | null>(null);
  const [pagina, setPagina] = React.useState(1);
  const [tamanhoPagina, setTamanhoPagina] = React.useState(25);
  const [periodo, setPeriodo] = React.useState<string | null>(null);
  const [periodoDe, setPeriodoDe] = React.useState('');
  const [periodoAte, setPeriodoAte] = React.useState('');
  const [acao, setAcao] = React.useState<string | null>(null);
  const [buscaAtor, setBuscaAtor] = React.useState('');
  const [buscaAtorDebounced, setBuscaAtorDebounced] = React.useState('');

  React.useEffect(() => {
    const idDebounce = window.setTimeout(() => setBuscaAtorDebounced(buscaAtor), DEBOUNCE_MS);
    return () => window.clearTimeout(idDebounce);
  }, [buscaAtor]);

  // Trocar de instalação ou qualquer filtro invalida a página atual — mesma
  // razão do filtro de Instalações (um offset além do fim do resultado novo).
  React.useEffect(() => {
    setPagina(1);
  }, [instalacao, periodoDe, periodoAte, acao, buscaAtorDebounced]);

  const buscar = React.useCallback(
    (signal?: AbortSignal) => {
      if (!instalacao) return;
      setEstado('carregando');
      adminDataSource
        .buscarAuditoriaInstalacao(
          instalacao.id,
          {
            de: periodoDe || undefined,
            ate: periodoAte || undefined,
            operacao: acao || undefined,
            ator: buscaAtorDebounced || undefined,
            limit: tamanhoPagina,
            offset: (pagina - 1) * tamanhoPagina,
          },
          signal,
        )
        .then((resultado) => {
          setItens(resultado.itens);
          setTotal(resultado.total);
          setEstado('pronto');
        })
        .catch((causa: unknown) => {
          if (causa instanceof DOMException && causa.name === 'AbortError') return;
          setErro(causa instanceof Error ? causa : new Error(String(causa)));
          setEstado('erro');
        });
    },
    [instalacao, periodoDe, periodoAte, acao, buscaAtorDebounced, pagina, tamanhoPagina],
  );

  React.useEffect(() => {
    const controller = new AbortController();
    buscar(controller.signal);
    return () => controller.abort();
  }, [buscar]);

  // Backend já limita a janela (teto de 31 dias) — presets curtos (1h/24h/7d)
  // continuam um refiltro só no cliente, sem ida ao servidor, igual Histórico.
  const eventosFiltrados = React.useMemo(
    () => itens.filter((item) => noPeriodo(new Date(item.criadoEm).getTime(), periodo, periodoDe, periodoAte)),
    [itens, periodo, periodoDe, periodoAte],
  );

  const totalPaginas = Math.max(1, Math.ceil(total / tamanhoPagina));

  const colunas: Array<Coluna<AuditoriaInstalacao>> = [
    {
      id: 'quando',
      cabecalho: 'Quando',
      largura: '150px',
      ordenarPor: (item) => item.criadoEm,
      celula: (item) => <span className="tabular-nums">{formatarDataHora(item.criadoEm)}</span>,
    },
    {
      id: 'acao',
      cabecalho: 'Ação',
      celula: (item) => {
        const { rotulo, icone: Icone } = ROTULO_OPERACAO[item.operacao];
        const detalhe = descreverDetalhe(item);
        return (
          <span className="flex items-center gap-1.5 font-bold">
            <Icone className="size-4 shrink-0 text-icon" aria-hidden="true" />
            {rotulo}
            {detalhe ? <Badge tone="info">{detalhe}</Badge> : null}
          </span>
        );
      },
    },
    {
      id: 'ator',
      cabecalho: 'Por',
      largura: '240px',
      ordenarPor: (item) => item.atorEmail,
      celula: (item) => <span className="text-on-surface-muted">{item.atorEmail}</span>,
    },
  ];

  return (
    <Dialog open={Boolean(instalacao)} onOpenChange={(aberto) => !aberto && aoFechar()}>
      <DialogContent className="z-[60] w-[min(60rem,96vw)] max-h-[85vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Auditoria — {instalacao?.nome}</DialogTitle>
          <DialogDescription>Personificação, habilitação e opt-in de beta, mais recentes primeiro.</DialogDescription>
        </DialogHeader>

        <TabelaPainel
          titulo="Eventos"
          contagem={estado === 'pronto' ? `${total} resultado(s)` : undefined}
          filtros={
            <FilterBar>
              <FilterBusca
                id="filtro-ator-auditoria"
                label="Buscar por ator"
                valor={buscaAtor}
                aoMudar={setBuscaAtor}
                placeholder="E-mail de quem fez a ação"
              />
              <FilterSelect
                id="filtro-acao-auditoria"
                label="Ação"
                valor={acao}
                aoMudar={setAcao}
                rotuloTodos="Qualquer ação"
                opcoes={Object.entries(ROTULO_OPERACAO).map(([valor, { rotulo }]) => ({ valor, rotulo }))}
              />
              <FilterPeriodo
                id="filtro-periodo-auditoria"
                periodo={periodo}
                de={periodoDe}
                ate={periodoAte}
                aoMudar={({ periodo: proximo, de, ate }) => {
                  setPeriodo(proximo);
                  setPeriodoDe(de);
                  setPeriodoAte(ate);
                }}
                presets={PERIODO_PRESETS}
                limiteDias={31}
              />
              <FilterLimpar
                ativo={periodo !== null || acao !== null || buscaAtor.trim() !== ''}
                quantidade={[periodo !== null, acao !== null, buscaAtor.trim() !== ''].filter(Boolean).length}
                aoLimpar={() => {
                  setPeriodo(null);
                  setPeriodoDe('');
                  setPeriodoAte('');
                  setAcao(null);
                  setBuscaAtor('');
                }}
              />
            </FilterBar>
          }
          paginacao={
            estado === 'pronto' ? (
              <Paginacao
                pagina={pagina}
                totalPaginas={totalPaginas}
                aoMudar={setPagina}
                tamanhoPagina={tamanhoPagina}
                aoMudarTamanho={setTamanhoPagina}
                tamanhosPagina={[10, 25, 50, 100]}
              />
            ) : undefined
          }
        >
          <DataTable
            rotulo="Auditoria da instalação"
            itens={eventosFiltrados}
            colunas={colunas}
            chave={(item) => `${item.criadoEm}|${item.operacao}|${item.atorEmail}`}
            estado={estado}
            erro={erro}
            aoTentarDeNovo={() => buscar()}
            alturaMaxima="46vh"
            vazio={{
              titulo: 'Nenhum evento registrado',
              descricao: 'Personificar, habilitar/desabilitar ou alternar beta nesta instalação vai aparecer aqui.',
            }}
          />
        </TabelaPainel>

        <DialogFooter>
          <Button variant="outline" onClick={aoFechar}>
            Fechar
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
